import { Search, Download, Loader2, Trash2, Mail } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { DANGER_BUTTON_CLASS } from "./DashboardStats";
import { ConfirmDangerDialog } from "./ConfirmDangerDialog";
import { ResendReminderDialog } from "./ResendReminderDialog";
import { fmtDate, PANEL_CLASS } from "./dashboardUtils";

// Reminder milestones in send order + their short labels for the history column.
const REMINDER_STEPS = [
  { key: "7d", label: "1w" },
  { key: "3d", label: "3d" },
  { key: "1d", label: "1d" },
];

const ReminderHistory = ({ sent }) => {
  const done = new Set(Array.isArray(sent) ? sent : []);
  if (done.size === 0) {
    return <span className="text-[#647384]">None yet</span>;
  }
  return (
    <div className="flex items-center gap-1">
      {REMINDER_STEPS.map((step) => {
        const isSent = done.has(step.key);
        return (
          <span
            key={step.key}
            title={isSent ? `${step.label} reminder sent` : `${step.label} reminder not sent`}
            className={
              isSent
                ? "inline-flex items-center justify-center min-w-[26px] h-6 px-1.5 rounded-md text-[11px] font-bold bg-[rgba(18,111,106,0.12)] text-[#126F6A] border border-[rgba(18,111,106,0.25)]"
                : "inline-flex items-center justify-center min-w-[26px] h-6 px-1.5 rounded-md text-[11px] font-semibold bg-transparent text-[#B7C0C9] border border-[rgba(17,42,67,0.12)]"
            }
          >
            {step.label}
          </span>
        );
      })}
    </div>
  );
};

const RegistrationRow = ({ item, onDelete, deleting, selected, onToggleSelect }) => {
  const attr = item.attribution || {};
  return (
    <tr
      className={`border-b border-[rgba(17,42,67,0.06)] hover:bg-[#FBF8F0] ${
        selected ? "bg-[rgba(31,145,136,0.06)]" : ""
      }`}
    >
      <td className="px-5 py-3 align-middle">
        <Checkbox
          data-testid={`dashboard-select-registration-${item.id}`}
          checked={selected}
          onCheckedChange={() => onToggleSelect(item.id)}
          aria-label={`Select registration for ${item.full_name || item.email}`}
        />
      </td>
      <td className="px-5 py-3 text-[#647384] whitespace-nowrap">{fmtDate(item.created_at)}</td>
      <td className="px-5 py-3 font-medium text-[#122131] whitespace-nowrap">
        {item.full_name}
        {item.marketing_consent && <span className="ml-2 text-[10px] uppercase font-bold text-[#126F6A]">opt-in</span>}
      </td>
      <td className="px-5 py-3 text-[#122131] whitespace-nowrap">
        <div>{item.email}</div>
        <div className="text-[#647384]">+65 {item.mobile}</div>
      </td>
      <td className="px-5 py-3 text-[#647384] whitespace-nowrap">{item.age_range || "\u2014"}</td>
      <td className="px-5 py-3 text-[#122131] whitespace-nowrap">{item.invited_by || "\u2014"}</td>
      <td className="px-5 py-3 text-[#647384] max-w-[220px] truncate" title={item.biggest_question || ""}>
        {item.biggest_question || "\u2014"}
      </td>
      <td className="px-5 py-3 text-[#647384] whitespace-nowrap">{attr.utm_source || "direct"}</td>
      <td className="px-5 py-3 whitespace-nowrap" data-testid={`dashboard-reminders-${item.id}`}>
        <ReminderHistory sent={item.reminders_sent} />
      </td>
      <td className="px-5 py-3 text-right whitespace-nowrap">
        <ConfirmDangerDialog
          dialogTestId={`delete-confirm-dialog-${item.id}`}
          cancelTestId={`delete-cancel-${item.id}`}
          confirmTestId={`delete-confirm-${item.id}`}
          title="Delete this registration?"
          description={`This removes ${item.full_name || "this registrant"} (${item.email}). You'll have a few seconds to undo it.`}
          confirmLabel="Delete"
          onConfirm={() => onDelete(item.id)}
          trigger={
            <button
              data-testid={`dashboard-delete-registration-${item.id}`}
              disabled={deleting}
              aria-label={`Delete registration for ${item.full_name || item.email}`}
              className="inline-flex items-center justify-center h-9 w-9 rounded-lg border border-[rgba(192,57,43,0.25)] text-[#c0392b] hover:bg-[rgba(192,57,43,0.08)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c0392b] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {deleting ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
            </button>
          }
        />
      </td>
    </tr>
  );
};

export const RegistrationsTable = ({
  items,
  total,
  query,
  onQueryChange,
  onExport,
  onClear,
  onDelete,
  deletingId,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  onDeleteSelected,
  bulkDeleting,
  onExportSelected,
  onResendSelected,
  resending,
  selectedItems,
  loading,
  clearing,
}) => {
  const visibleIds = items.map((it) => it.id);
  const selectedVisible = visibleIds.filter((id) => selectedIds.has(id));
  const selectedCount = selectedIds.size;
  const allSelected = visibleIds.length > 0 && selectedVisible.length === visibleIds.length;
  const headerChecked = allSelected ? true : selectedVisible.length > 0 ? "indeterminate" : false;

  return (
    <div className={PANEL_CLASS}>
      <div className="p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b border-[rgba(17,42,67,0.08)]">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="text-lg font-bold text-[#122131]">Registrations ({items.length})</h2>
          {selectedCount > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium text-[#126F6A]" data-testid="dashboard-selected-count">
                {selectedCount} selected
              </span>
              <button
                data-testid="dashboard-export-selected"
                onClick={onExportSelected}
                className="inline-flex items-center gap-2 h-9 px-3 rounded-xl border border-[rgba(17,42,67,0.18)] text-sm font-semibold text-[#122131] hover:bg-[#FBF8F0] transition-colors"
              >
                <Download size={16} /> Export selected
              </button>
              <ResendReminderDialog
                count={selectedCount}
                selectedItems={selectedItems}
                resending={resending}
                onConfirm={onResendSelected}
                trigger={
                  <button
                    data-testid="dashboard-resend-reminder"
                    disabled={resending || loading}
                    className="inline-flex items-center gap-2 h-9 px-3 rounded-xl bg-[#F7B838] text-[#112A43] text-sm font-semibold hover:bg-[#e6a821] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {resending ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />} Resend reminder
                  </button>
                }
              />
              <ConfirmDangerDialog
                dialogTestId="bulk-delete-confirm-dialog"
                cancelTestId="bulk-delete-cancel"
                confirmTestId="bulk-delete-confirm"
                title={`Delete ${selectedCount} selected registration${selectedCount === 1 ? "" : "s"}?`}
                description={`This removes ${selectedCount} selected registration${selectedCount === 1 ? "" : "s"}. You'll have a few seconds to undo it.`}
                confirmLabel={`Delete ${selectedCount}`}
                onConfirm={onDeleteSelected}
                trigger={
                  <button
                    data-testid="dashboard-bulk-delete"
                    disabled={bulkDeleting || loading}
                    className={DANGER_BUTTON_CLASS}
                  >
                    {bulkDeleting ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />} Delete selected ({selectedCount})
                  </button>
                }
              />
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#647384]" />
            <input
              data-testid="dashboard-search"
              value={query}
              onChange={(e) => onQueryChange(e.target.value)}
              placeholder="Search name, email, mobile..."
              className="h-10 w-full sm:w-64 rounded-xl border border-[rgba(17,42,67,0.18)] pl-9 pr-3 text-sm text-[#122131] focus:outline-none focus:border-[#1F9188]"
            />
          </div>
          <button
            data-testid="dashboard-export-csv"
            onClick={onExport}
            className="inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-[#1F9188] text-white text-sm font-semibold hover:bg-[#126F6A] transition-colors"
          >
            <Download size={16} /> Export CSV
          </button>
          <ConfirmDangerDialog
            dialogTestId="clear-confirm-dialog"
            cancelTestId="clear-cancel"
            confirmTestId="clear-confirm"
            title="Clear all registrations?"
            description={`This permanently deletes all ${total} registration${total === 1 ? "" : "s"} for this event. This action cannot be undone. Consider exporting a CSV backup first.`}
            confirmLabel="Delete all"
            onConfirm={onClear}
            trigger={
              <button
                data-testid="dashboard-clear-registrations"
                disabled={loading || clearing || total === 0}
                className={DANGER_BUTTON_CLASS}
              >
                {clearing ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />} Clear
              </button>
            }
          />
        </div>
      </div>

      <div className="overflow-x-auto">
        {items.length === 0 ? (
          <div className="p-10 text-center text-[#647384]">
            {total === 0 ? "No registrations yet." : "No results match your search."}
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="dashboard-table">
            <thead>
              <tr className="text-left text-[#647384] border-b border-[rgba(17,42,67,0.08)]">
                <th className="px-5 py-3 font-medium">
                  <Checkbox
                    data-testid="dashboard-select-all"
                    checked={headerChecked}
                    onCheckedChange={(checked) => onToggleSelectAll(visibleIds, checked === true)}
                    aria-label="Select all registrations"
                  />
                </th>
                <th className="px-5 py-3 font-medium">Registered</th>
                <th className="px-5 py-3 font-medium">Name</th>
                <th className="px-5 py-3 font-medium">Contact</th>
                <th className="px-5 py-3 font-medium">Age</th>
                <th className="px-5 py-3 font-medium">Invited By</th>
                <th className="px-5 py-3 font-medium">Question</th>
                <th className="px-5 py-3 font-medium">Source</th>
                <th className="px-5 py-3 font-medium">Reminders</th>
                <th className="px-5 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <RegistrationRow
                  key={it.id}
                  item={it}
                  onDelete={onDelete}
                  deleting={deletingId === it.id}
                  selected={selectedIds.has(it.id)}
                  onToggleSelect={onToggleSelect}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};
